import UserAvatar from "./UserAvatar";
import {GlobalSearch} from './PlatformTools';
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  FaBell,
  FaBars,
  FaQuestionCircle,
  FaSearch,
  FaSyncAlt,
  FaVolumeUp,
  FaVolumeMute,
  FaUserShield,
  FaClock,
  FaTimes,
  FaChevronRight,
} from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Topbar.css';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';


interface Notification {
  id: number;
  researchApplicationId?: number;
  type?: string; title?: string; priority?: string; actingCapacity?: string; targetRoute?: string;
  message: string;
  createdAt: string;
  read: boolean;
}

interface AppDetailsLite {
  id?: number;
  applicantUserId?: number | null;
  facultyCode?: string | null;
  departmentCode?: string | null;
  stage?: string | null;
  status?: string | null;
  nextApproverIds?: number[];
  raw?: any;
}

type NotificationTab = 'unread' | 'all';

const notificationSoundUrl = '/notification.mp3';

const USER_TYPE_LABELS: Record<string, string> = {
  ADMIN_CYBER: 'System Admin',
  RESEARCH_CHAIR: 'Research Ethics Committee Chair',
  PROFESSOR: 'Professor',
  RESEARCH_OFFICER: 'Research Officer',
  FACULTY_RAP: 'Faculty Representative',
  FACULTY_ADMIN: 'Faculty Admin',
  INSTITUTE_REPRESENTATIVE: 'Institute Representative',
  DEPARTMENT: 'Department Representative',
  APPLICANT: 'Applicant',
  ADMIN: 'Executive Director RID',
  REVIEWER: 'Research Ethics Committee Secretary',
};

const getUserTypeLabel = (userType?: string | null) => {
  if (!userType) return 'User';
  return USER_TYPE_LABELS[String(userType).trim().toUpperCase()] || String(userType);
};

const getInitials = (acct: any) => {
  if (!acct) return '';
  const first = (acct?.name || acct?.firstName || '').trim();
  const last = (acct?.surname || acct?.lastName || '').trim();
  return ((first ? first[0] : '') + (last ? last[0] : '')).toUpperCase();
};

const safeUpper = (value: any) => String(value || '').trim().toUpperCase();

const asNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const uniqNumbers = (values: any[]): number[] => {
  const out: number[] = [];
  values.forEach((v) => {
    const n = asNumber(v);
    if (n !== null && !out.includes(n)) out.push(n);
  });
  return out;
};

const pickFirstNumber = (...values: any[]): number | null => {
  for (const value of values) {
    const n = asNumber(value);
    if (n !== null) return n;
  }
  return null;
};

const pickFirstString = (...values: any[]): string | null => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }
  return null;
};

const extractApproverIds = (raw: any): number[] => {
  if (!raw || typeof raw !== 'object') return [];

  const ids: any[] = [
    raw?.nextApproverId,
    raw?.currentApproverId,
    raw?.approverId,
    raw?.assignedTo,
    raw?.assignedUserId,
    raw?.selectedApproverId,
    raw?.selectedReviewerId,
    raw?.reviewerId,
    raw?.reviewerUserId,
    raw?.researchChairId,
    raw?.chairId,
    raw?.facultyRapId,
    raw?.facultyAdminId,
    raw?.departmentApproverId,
    raw?.departmentUserId,
    raw?.finalApproverId,
    raw?.adminApproverId,
    raw?.cyberApproverId,

    raw?.nextApprover?.id,
    raw?.approver?.id,
    raw?.reviewer?.id,
    raw?.researchChair?.id,
    raw?.assignedUser?.id,
    raw?.selectedApprover?.id,
    raw?.facultyRap?.id,
    raw?.facultyAdmin?.id,
    raw?.departmentApprover?.id,
    raw?.finalApprover?.id,
    raw?.admin?.id,
    raw?.cyberAdmin?.id,
  ];

  if (Array.isArray(raw?.approvers)) {
    raw.approvers.forEach((item: any) => ids.push(item?.id, item?.userId));
  }

  if (Array.isArray(raw?.reviewers)) {
    raw.reviewers.forEach((item: any) => ids.push(item?.id, item?.userId));
  }

  if (Array.isArray(raw?.selectedApprovers)) {
    raw.selectedApprovers.forEach((item: any) => ids.push(item?.id, item?.userId));
  }

  return uniqNumbers(ids);
};

const normalizeApplicationDetails = (raw: any): AppDetailsLite => {
  const applicantUserId = pickFirstNumber(
    raw?.applicantUserId,
    raw?.userId,
    raw?.createdBy,
    raw?.applicantId,
    raw?.submittedBy,
    raw?.ownerId,
    raw?.applicant?.id,
    raw?.user?.id
  );

  const facultyCode = pickFirstString(
    raw?.facultyCode,
    raw?.faculty?.code,
    raw?.faculty_code,
    raw?.applicantFacultyCode,
    raw?.user?.facultyCode,
    raw?.applicant?.facultyCode
  );

  const departmentCode = pickFirstString(
    raw?.departmentCode,
    raw?.department?.code,
    raw?.department_code,
    raw?.applicantDepartmentCode,
    raw?.user?.departmentCode,
    raw?.applicant?.departmentCode
  );

  const stage = pickFirstString(
    raw?.stage,
    raw?.currentStage,
    raw?.workflowStage,
    raw?.approvalStage
  );

  const status = pickFirstString(
    raw?.status,
    raw?.applicationStatus,
    raw?.approvalStatus
  );

  return {
    id: pickFirstNumber(raw?.id) ?? undefined,
    applicantUserId,
    facultyCode,
    departmentCode,
    stage,
    status,
    nextApproverIds: extractApproverIds(raw),
    raw,
  };
};

const isOwnApplication = (app: AppDetailsLite | undefined, userId?: number) =>
  !!app && !!userId && app.applicantUserId === userId;

const isSameFaculty = (app: AppDetailsLite | undefined, facultyCode?: string) =>
  !!app &&
  !!facultyCode &&
  safeUpper(app.facultyCode) === safeUpper(facultyCode);

const isSameDepartment = (
  app: AppDetailsLite | undefined,
  departmentCode?: string
) =>
  !!app &&
  !!departmentCode &&
  safeUpper(app.departmentCode) === safeUpper(departmentCode);

const isAssignedNextApprover = (
  app: AppDetailsLite | undefined,
  userId?: number
) =>
  !!app &&
  !!userId &&
  Array.isArray(app.nextApproverIds) &&
  app.nextApproverIds.includes(userId);

const stageMatchesReviewer = (app?: AppDetailsLite) => {
  const stage = safeUpper(app?.stage);
  const status = safeUpper(app?.status);
  const text = `${stage} ${status}`;

  return (
    text.includes('SEND_FOR_REVIEW') ||
    text.includes('REVIEW') ||
    text.includes('FACULTY_APPROVED') ||
    text.includes('FACULTY_RECOMMENDED') ||
    text.includes('APPROVED_BY_FACULTY') ||
    text.includes('READY_FOR_REVIEW')
  );
};

const stageMatchesResearchChair = (app?: AppDetailsLite) => {
  const stage = safeUpper(app?.stage);
  const status = safeUpper(app?.status);
  const text = `${stage} ${status}`;

  return (
    text.includes('RESEARCH_CHAIR') ||
    text.includes('CHAIR') ||
    text.includes('READY_FOR_CHAIR') ||
    text.includes('PENDING_CHAIR_APPROVAL')
  );
};

const stageMatchesFinalization = (app?: AppDetailsLite) => {
  const stage = safeUpper(app?.stage);
  const status = safeUpper(app?.status);
  const text = `${stage} ${status}`;

  return (
    text.includes('SEND_TO_FINAL') ||
    text.includes('FINAL') ||
    text.includes('READY_FOR_FINAL') ||
    text.includes('REVIEWED') ||
    text.includes('PENDING_FINAL_APPROVAL')
  );
};

const relTime = (iso: string) => {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '';
  const diff = Date.now() - ts;
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;

  if (abs < minute) return 'just now';
  if (abs < hour) return `${Math.floor(abs / minute)}m ago`;
  if (abs < day) return `${Math.floor(abs / hour)}h ago`;
  return `${Math.floor(abs / day)}d ago`;
};

const truncate = (msg: string, len = 88) =>
  msg.length > len ? `${msg.slice(0, len)}…` : msg;

const Topbar: React.FC<{onToggleSidebar:()=>void}> = ({onToggleSidebar}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, logout } = useAuth();

  const acct = account as any;
  const userId: number | undefined = asNumber(acct?.id) ?? undefined;
  const userType: string = safeUpper(acct?.userType);
  const facultyCode: string | undefined = acct?.facultyCode || undefined;
  const departmentCode: string | undefined = acct?.departmentCode || undefined;
  const displayUserType = getUserTypeLabel(acct?.userType);
  const fullName =
    `${acct?.name || acct?.firstName || ''} ${acct?.surname || acct?.lastName || ''}`.trim() ||
    'User';

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSearch, setShowSearch] = useState(false);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeNotification, setActiveNotification] = useState<Notification | null>(null);
  const [activeTab, setActiveTab] = useState<NotificationTab>('unread');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    return localStorage.getItem('notifSoundEnabled') !== 'false';
  });

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [appMetaMap, setAppMetaMap] = useState<Record<number, AppDetailsLite>>({});
  const [pulse, setPulse] = useState(false);
  const [badgeSlideIn, setBadgeSlideIn] = useState(false);

  const unreadCountRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fetchCtlRef = useRef<AbortController | null>(null);

  const initials = getInitials(acct) || 'U';

  useEffect(() => {
    localStorage.setItem('notifSoundEnabled', String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const getBaseNotificationUrls = useCallback((): string[] => ['/v1/api/notifications'], []);

  const fetchJsonFromCandidates = useCallback(async (urls: string[]) => {
    for (const url of urls) {
      try {
        const res = await fetch(url, {
          signal: fetchCtlRef.current?.signal,
        });
        if (!res.ok) continue;

        const json = await res.json();

        if (Array.isArray(json)) return json;
        if (Array.isArray(json?.data)) return json.data;
        if (Array.isArray(json?.notifications)) return json.notifications;
      } catch (error: any) {
        if (error?.name === 'AbortError') throw error;
      }
    }
    return [];
  }, []);

  const fetchApplicationDetails = useCallback(
    async (applicationId: number): Promise<AppDetailsLite | null> => {
      try {
        const endpoints = [
          `/v1/api/research-applications/details/${applicationId}`,
          `/v1/api/research-applications/${applicationId}`,
        ];

        for (const endpoint of endpoints) {
          const res = await fetch(endpoint, {
            signal: fetchCtlRef.current?.signal,
          });
          if (!res.ok) continue;

          const json = await res.json();
          const raw =
            json?.data?.application ||
            json?.data ||
            json?.application ||
            json;

          if (raw) return normalizeApplicationDetails(raw);
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') throw error;
      }

      return null;
    },
    []
  );

  const shouldShowNotification = useCallback(
    (app?: AppDetailsLite) => {
      if (!acct) return false;

      const own = isOwnApplication(app, userId);
      const sameFaculty = isSameFaculty(app, facultyCode);
      const sameDepartment = isSameDepartment(app, departmentCode);
      const nextApprover = isAssignedNextApprover(app, userId);

      switch (userType) {
        case 'RESEARCH_OFFICER':
          return true;

        case 'APPLICANT':
          return own;

        case 'DEPARTMENT':
          return own || sameDepartment || nextApprover;

        case 'FACULTY_RAP':
        case 'FACULTY_ADMIN':
        case 'INSTITUTE_REPRESENTATIVE':
          return own || sameFaculty || nextApprover;

        case 'RESEARCH_CHAIR':
          return own || nextApprover || stageMatchesResearchChair(app);

        case 'REVIEWER':
          return own || nextApprover || stageMatchesReviewer(app);

        case 'ADMIN':
        case 'ADMIN_CYBER':
          return own || nextApprover || stageMatchesFinalization(app);

        default:
          return own || nextApprover;
      }
    },
    [acct, userId, userType, facultyCode, departmentCode]
  );

  const fetchNotifications = useCallback(
    async (manual = false) => {
      if (!acct) return;

      try {
        fetchCtlRef.current?.abort();
        const ctl = new AbortController();
        fetchCtlRef.current = ctl;

        if (manual) setIsRefreshing(true);
        setLoadingNotifications(true);

        const rawNotifications = await fetchJsonFromCandidates(getBaseNotificationUrls());

        const normalizedNotifications: Notification[] = Array.isArray(rawNotifications)
          ? rawNotifications
              .map((item: any) => ({
                id: Number(item?.id),
                researchApplicationId: item?.researchApplicationId
                  ? Number(item.researchApplicationId)
                  : undefined,
                message: String(item?.message || ''),
                type:item.type,title:item.title,priority:item.priority,actingCapacity:item.actingCapacity,targetRoute:item.targetRoute,
                createdAt: String(item?.createdAt || item?.dateCreated || new Date().toISOString()),
                read: Boolean(item?.read),
              }))
              .filter((item) => Number.isFinite(item.id) && item.message)
          : [];

        const appIds = Array.from(
          new Set(
            normalizedNotifications
              .map((n) => n.researchApplicationId)
              .filter((id): id is number => Number.isFinite(id))
          )
        );

        const metaEntries = await Promise.all(
          appIds.map(async (appId) => {
            const details = await fetchApplicationDetails(appId);
            return [appId, details] as const;
          })
        );

        const nextMetaMap: Record<number, AppDetailsLite> = {};
        metaEntries.forEach(([appId, details]) => {
          if (details) nextMetaMap[appId] = details;
        });

        const filtered = normalizedNotifications;

        filtered.sort((a, b) => {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        const nextUnreadCount = filtered.filter((n) => !n.read).length;
        if (nextUnreadCount > unreadCountRef.current) {
          if (soundEnabled) {
            audioRef.current?.play().catch(() => {});
          }
          setPulse(true);
          setBadgeSlideIn(true);
          window.setTimeout(() => setPulse(false), 1400);
          window.setTimeout(() => setBadgeSlideIn(false), 900);
        }
        unreadCountRef.current = nextUnreadCount;

        setAppMetaMap(nextMetaMap);
        setNotifications(filtered);
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Failed to fetch notifications:', error);
        }
      } finally {
        setLoadingNotifications(false);
        setIsRefreshing(false);
      }
    },
    [
      acct,
      userType,
      soundEnabled,
      getBaseNotificationUrls,
      fetchJsonFromCandidates,
      fetchApplicationDetails,
      shouldShowNotification,
    ]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    let intervalId: number | undefined;

    const startPolling = () => {
      const interval = document.visibilityState === 'visible' ? 30_000 : 120_000;
      intervalId = window.setInterval(() => fetchNotifications(), interval);
    };

    const onVisibilityChange = () => {
      if (intervalId) clearInterval(intervalId);
      startPolling();
    };

    startPolling();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      fetchCtlRef.current?.abort();
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
    setShowSearch(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target as HTMLElement)?.isContentEditable || (e as any).isComposing) {
        return;
      }

      if (e.key === '/' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        setShowSearch(true);
      }

      if (e.key.toLowerCase() === 'n') {
        setShowNotifications((prev) => !prev);
      }

      if (e.key === '?') {
        navigate('/help');
      }

      if (e.key === 'Escape') {
        setShowNotifications(false);
        setShowUserMenu(false);
        setShowSearch(false);
        setActiveNotification(null);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.user-wrapper')) setShowUserMenu(false);
      if (!target.closest('.notification-wrapper')) setShowNotifications(false);
      if (!target.closest('.topbar-search-form') && !target.closest('.topbar-search-toggle') && !target.closest('dialog')) {
        setShowSearch(false);
      }
    };

    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.read),
    [notifications]
  );

  const visibleNotifications = useMemo(() => {
    return activeTab === 'unread' ? unreadNotifications : notifications;
  }, [activeTab, unreadNotifications, notifications]);

  const unreadCount = unreadNotifications.length;
  const hasUnread = unreadCount > 0;

  const markSingleAsRead = useCallback(async (notificationId: number) => {
    try {
      const response=await fetch(`/v1/api/notifications/${notificationId}/read`, {
        method: 'PUT',
      });
      if(!response.ok)throw new Error("Unable to mark notification read");

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId ? { ...item, read: true } : item
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const handleNotificationClick = async (notification: Notification) => {
    setActiveNotification(notification.targetRoute?null:notification);
    setShowNotifications(false);

    if (!notification.read) {
      await markSingleAsRead(notification.id);
    }

    if (notification.targetRoute || notification.researchApplicationId) {
      navigate(notification.targetRoute || `/applications?highlight=${notification.researchApplicationId}`);
    }
  };

  const handleCloseNotificationPopup = () => {
    setActiveNotification(null);
  };

  const handleMarkAllRead = async () => {
    if (!unreadCount) return;

    try {
      const response=await fetch('/v1/api/notifications/read-all',{method:'PUT'});if(!response.ok)throw new Error('Unable to mark notifications read');

      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
      unreadCountRef.current = 0;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenApplication = (notification: Notification) => {
    setActiveNotification(null);
    if (!notification.read) {
      markSingleAsRead(notification.id);
    }
    if (notification.researchApplicationId) {
      navigate(`/applications?highlight=${notification.researchApplicationId}`);
    }
  };

  return (
    <header className="topbar" role="banner">
      <audio ref={audioRef} src={notificationSoundUrl} preload="auto" />

      <div className="topbar-left">
        <button type="button" className="icon-btn topbar-mobile-menu" onClick={onToggleSidebar} aria-label="Toggle navigation menu"><FaBars /></button>
        <div className="topbar-brand-block">
          <div className="topbar-page-title">{location.pathname.split('/')[1]?.replace(/-/g,' ') || 'Research workspace'}</div>
          <div className="topbar-subline">
            <span className="role-chip">
              <FaUserShield />
              <span>{displayUserType}</span>
            </span>

            {!!facultyCode && <span className="context-chip">Faculty: {facultyCode}</span>}
            {!!departmentCode && <span className="context-chip">Dept: {departmentCode}</span>}
          </div>
        </div>
      </div>

      <div className="topbar-center">
        <button
          className="icon-btn topbar-search-toggle"
          onClick={() => setShowSearch((v) => !v)}
          aria-expanded={showSearch}
          aria-controls="topbar-search"
          title="Search (/ or Ctrl/Cmd+K)"
        >
          <FaSearch className="topbar-icon" />
          <span className="topbar-search-label">Search workspace</span><kbd>Ctrl K</kbd>
        </button>

        {showSearch && (
          <GlobalSearch onClose={()=>setShowSearch(false)}/>
        )}
      </div>

      <div className="topbar-right">
        <Link to="/applications/new" className="topbar-create" aria-label="New application">+ <span>New application</span></Link>
        <ThemeToggle />
        <div className="topbar-item time-card" title="Current Date & Time" aria-live="polite">
          <FaClock className="topbar-icon subtle" />
          <span>{currentTime.toLocaleString()}</span>
        </div>

        <button
          className={`topbar-item icon-btn refresh-btn ${isRefreshing ? 'spinning' : ''}`}
          onClick={() => fetchNotifications(true)}
          title="Refresh notifications"
          aria-label="Refresh notifications"
        >
          <FaSyncAlt className="topbar-icon" />
        </button>

        <Link
          to="/help"
          className="topbar-item icon-btn"
          title="Help & FAQ"
          aria-label="Help and FAQ"
        >
          <FaQuestionCircle className="topbar-icon" />
        </Link>

        <div className="topbar-item notification-wrapper">
          <button
            className={`icon-btn notification-btn ${pulse ? 'pulse' : ''} ${hasUnread ? 'has-unread' : ''}`}
            data-state={hasUnread ? 'unread' : 'empty'}
            onClick={() => setShowNotifications((v) => !v)}
            aria-expanded={showNotifications}
            aria-haspopup="true"
            aria-label={hasUnread ? `${unreadCount} new notifications` : 'Notifications'}
            title={hasUnread ? `${unreadCount} new notifications` : 'Notifications'}
          >
            <FaBell className="topbar-icon" />
            {hasUnread && (
              <span
                className={`badge ${badgeSlideIn ? 'slide-in' : ''}`}
                aria-label={`${unreadCount} unread`}
                aria-live="polite"
              >
                {unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown" role="dialog" aria-label="Notifications">
              <div className="notification-header">
                <div className="notification-title-group">
                  <span className="notification-title">Notifications</span>
                  <span className="notification-count-pill">{notifications.length}</span>
                </div>

                <div className="notification-tools">
                  <button
                    className="sound-toggle-btn"
                    type="button"
                    onClick={() => setSoundEnabled((prev) => !prev)}
                    title={soundEnabled ? 'Mute notification sound' : 'Enable notification sound'}
                    aria-label={soundEnabled ? 'Mute notification sound' : 'Enable notification sound'}
                  >
                    {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
                  </button>

                  <button
                    className={`mini-refresh-btn ${loadingNotifications ? 'spinning' : ''}`}
                    type="button"
                    onClick={() => fetchNotifications(true)}
                    title="Refresh"
                    aria-label="Refresh notifications"
                  >
                    <FaSyncAlt />
                  </button>

                  <button
                    className="mark-all-btn"
                    onClick={handleMarkAllRead}
                    disabled={unreadCount === 0}
                    title="Mark all as read"
                  >
                    Mark all
                  </button>
                </div>
              </div>

              <div className="notification-tabs">
                <button
                  className={`notification-tab ${activeTab === 'unread' ? 'active' : ''}`}
                  onClick={() => setActiveTab('unread')}
                  type="button"
                >
                  Unread
                  {unreadCount > 0 && <span className="tab-count">{unreadCount}</span>}
                </button>

                <button
                  className={`notification-tab ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                  type="button"
                >
                  All
                  <span className="tab-count">{notifications.length}</span>
                </button>
              </div>

              <Link className="notification-tab" to="/notifications" onClick={() => setShowNotifications(false)}>Open Notification Centre</Link>

              {loadingNotifications ? (
                <div className="no-notifs">Loading notifications...</div>
              ) : visibleNotifications.length ? (
                <div className="notification-list">
                  {visibleNotifications.map((notification, notificationIndex) => {
                    const app =
                      notification.researchApplicationId !== undefined
                        ? appMetaMap[notification.researchApplicationId]
                        : undefined;

                    return (
                      <button
                        key={notification.id}
                        className={`notification-item ${notification.read ? 'is-read' : 'is-unread'}`}
                        onClick={() => handleNotificationClick(notification)}
                        title="Open notification"
                        type="button"
                      >
                        {(notificationIndex===0 || new Date(visibleNotifications[notificationIndex-1].createdAt).toDateString()!==new Date(notification.createdAt).toDateString()) && <small>{new Date(notification.createdAt).toDateString()===new Date().toDateString()?'Today':'Earlier'}</small>}
                        <small>{notification.actingCapacity?.replace(/_/g,' ')} · {notification.priority}</small>
                        <div className="notification-item-top">
                          <div className="notif-message">
                            {truncate(notification.message)}
                          </div>
                          {!notification.read && <span className="notif-unread-dot" />}
                        </div>

                        <div className="notif-meta">
                          <span>{relTime(notification.createdAt)}</span>

                          {notification.researchApplicationId ? (
                            <span className="notif-app-pill">
                              App #{notification.researchApplicationId}
                            </span>
                          ) : null}

                          {app?.facultyCode ? (
                            <span className="notif-scope-pill">
                              {app.facultyCode}
                            </span>
                          ) : null}

                          {app?.departmentCode ? (
                            <span className="notif-scope-pill">
                              {app.departmentCode}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="no-notifs">
                  {activeTab === 'unread'
                    ? 'No unread notifications'
                    : 'No notifications available'}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="topbar-item user-wrapper">
          <button
            className="icon-btn profile-btn"
            onClick={() => setShowUserMenu((v) => !v)}
            aria-haspopup="true"
            aria-expanded={showUserMenu}
            aria-label="Profile menu"
          >
            <span className="profile-avatar" aria-hidden>
              <UserAvatar userId={acct?.id} name={`${acct?.name||''} ${acct?.surname||''}`.trim() || initials}/>
            </span>
          </button>

          {showUserMenu && (
            <div className="user-menu-dropdown" role="menu" aria-label="User menu">
              <div className="user-info-block">
                <div className="user-details">
                  <div className="user-fullname">{fullName}</div>
                  <div className="user-email">{acct?.email || ''}</div>
                  <div className="user-role">{displayUserType}</div>
                </div>
              </div>

              <Link
                to="/user-profile"
                className="user-menu-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
              >
                Profile
              </Link>
              <Link to="/applications/new" className="user-menu-item" role="menuitem">New application</Link>
              <Link to="/my-applications" className="user-menu-item" role="menuitem">My applications</Link>
              <Link to="/action-centre" className="user-menu-item" role="menuitem">Action Centre</Link>
              <Link to="/help" className="user-menu-item" role="menuitem">Help and FAQ</Link>

              <button className="user-menu-item danger" role="menuitem" onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {activeNotification && (
        <ModalLayer
          className="notification-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Notification"
        >
          <div className="notification-popup">
            <div className="notification-popup-header">
              <h3>Notification</h3>
              <button
                className="popup-close-icon"
                onClick={handleCloseNotificationPopup}
                aria-label="Close notification"
              >
                <FaTimes />
              </button>
            </div>

            <p className="notification-popup-text">{activeNotification.message}</p>

            <div className="notification-popup-actions">
              {activeNotification.researchApplicationId ? (
                <button
                  className="open-app-btn"
                  onClick={() => handleOpenApplication(activeNotification)}
                >
                  Open application <FaChevronRight />
                </button>
              ) : null}

              <button className="close-btn" onClick={handleCloseNotificationPopup}>
                Close
              </button>
            </div>
          </div>
        </ModalLayer>
      )}
    </header>
  );
};

export default Topbar;
